import { type ReactNode, useId } from 'react';

import { cx } from '@utils/cx';

import styles from './Section.module.scss';

interface SectionProps {
  readonly eyebrow?: string;
  readonly title?: ReactNode;
  readonly lede?: string;
  readonly align?: 'start' | 'center';
  readonly className?: string;
  readonly children?: ReactNode;
}

/**
 * A page section: optional eyebrow, heading and lede, then content, all in
 * the standard container. The heading labels the section for assistive
 * technology, so a screen-reader user can jump between sections by name.
 */
export const Section = ({
  eyebrow,
  title,
  lede,
  align = 'start',
  className,
  children,
}: SectionProps) => {
  const headingId = useId();

  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={cx(styles.section, className)}
    >
      {(eyebrow ?? title ?? lede) && (
        <header className={styles.header} data-align={align}>
          {eyebrow && (
            <p className={styles.eyebrow} data-reveal="">
              {eyebrow}
            </p>
          )}
          {title && (
            <h2 id={headingId} className={styles.title} data-reveal="">
              {title}
            </h2>
          )}
          {lede && (
            <p className={styles.lede} data-reveal="">
              {lede}
            </p>
          )}
        </header>
      )}
      {children}
    </section>
  );
};
